// src/lib/a11y/input-modality.ts
//
// Document-wide input-modality tracker (the `what-input` pattern): is the user
// currently driving with the KEYBOARD or a POINTER (mouse/touch)? The listeners
// install SYNCHRONOUSLY at module load (client-only — see the bottom of this
// file) and then always listen, so a consumer that mounts AFTER the interaction
// that concerns it — e.g. the lazy site-search palette, which only mounts once
// search is opened — still reads the modality of the very event that opened it
// (a `pointerdown` on the trigger, or the ⌘K `keydown`), which a per-mount
// listener would miss. Installing at import time (rather than in a post-paint
// `useEffect`) also closes the hydration→effect race: a pointer open in that
// window would otherwise read the default `keyboard` and wrongly show the ring.
//
// Why this exists: browsers force `:focus-visible` to match on a text input
// even for a mouse click, so a `focus-visible:` ring can't be hidden on
// pointer. To keep the keyboard affordance (WCAG) while removing the ring for
// pointer users, the palette gates its ring on this modality instead.

export type InputModality = 'keyboard' | 'pointer'

// Default to keyboard: the safe a11y bias is to SHOW focus affordances until we
// have positive evidence the user is on a pointer.
let modality: InputModality = 'keyboard'
let installed = false
const listeners = new Set<(m: InputModality) => void>()

function set(next: InputModality): void {
  if (next === modality) return
  modality = next
  for (const listener of listeners) listener(next)
}

function install(): void {
  if (installed || typeof window === 'undefined') return
  installed = true
  // Capture phase so the modality is observed even when a downstream handler
  // stops propagation (the ⌘K listener calls `stopImmediatePropagation`, but in
  // the bubble phase — capture always runs first). `passive` because we never
  // preventDefault here; this only observes.
  const opts: AddEventListenerOptions = { capture: true, passive: true }
  window.addEventListener('keydown', () => set('keyboard'), opts)
  window.addEventListener('pointerdown', () => set('pointer'), opts)
  window.addEventListener('touchstart', () => set('pointer'), opts)
  // Belt-and-suspenders: some environments dispatch `mousedown` without a
  // preceding `pointerdown` (older engines, synthetic events, certain
  // automation/assistive layers), so a pointer open would otherwise read the
  // default `keyboard`. Observing `mousedown` too closes that gap.
  window.addEventListener('mousedown', () => set('pointer'), opts)
}

/**
 * Eagerly install the document-wide listeners. Call this from an always-mounted
 * client component (e.g. the navbar's `SiteSearch`) so the tracker is already
 * listening BEFORE a lazy consumer mounts — otherwise the interaction that
 * triggers the mount (the `pointerdown` that opens the palette) fires before the
 * listeners exist and is missed, and the first open reads the wrong modality.
 */
export function ensureInputModalityTracking(): void {
  install()
}

/** Current modality. Installs the listeners on first call. */
export function getInputModality(): InputModality {
  install()
  return modality
}

/** Subscribe to modality changes; returns an unsubscribe. Installs on first call. */
export function subscribeInputModality(callback: (m: InputModality) => void): () => void {
  install()
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

// Install synchronously at module load on the client so the tracker is already
// listening before any consumer's effect runs (no hydration→effect race) and
// before the interaction that opens a lazy consumer fires. Guarded on
// `document` so this module stays import-safe under SSR / Node (no top-level
// `window`/`document` access that would throw); `install()` itself is
// idempotent and also guards on `window`.
if (typeof document !== 'undefined') install()
